import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import { PORTAL_API_BASE_URL } from '@echospaces-web-monorepo/es-portal-shell';

export interface Island {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  thumbnailUrl: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

@Injectable({ providedIn: 'root' })
export class IslandService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(PORTAL_API_BASE_URL);

  private readonly _islands = signal<Island[]>([]);
  private readonly _loading = signal(false);
  private readonly _selectedIsland = signal<Island | null>(null);

  readonly islands = this._islands.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly selectedIsland = this._selectedIsland.asReadonly();

  async loadIslands(): Promise<void> {
    this._loading.set(true);
    try {
      const data = await lastValueFrom(this.http.get<Island[]>(`${this.api}/islands`));
      this._islands.set(data);
    } finally {
      this._loading.set(false);
    }
  }

  async loadIsland(id: string): Promise<void> {
    this._loading.set(true);
    try {
      const data = await lastValueFrom(this.http.get<Island>(`${this.api}/islands/${id}`));
      this._selectedIsland.set(data);
    } finally {
      this._loading.set(false);
    }
  }

  async createIsland(dto: { name: string; slug: string; description?: string }): Promise<Island> {
    const island = await lastValueFrom(this.http.post<Island>(`${this.api}/islands`, dto));
    this._islands.update(list => [...list, island]);
    return island;
  }
}
